// Cadências de follow-up (material do time — textos reproduzidos literalmente).
// Usadas na aba "Cadências" e no modal "Preparar e-mail de follow-up".

export type CadenceChannel = "E-mail" | "LinkedIn" | "LinkedIn/E-mail" | "WhatsApp";

export interface CadenceTouch {
  n: number;
  dia: string;
  canal: CadenceChannel;
  objetivo: string;
  assunto: string; // vazio para toques de WhatsApp/LinkedIn sem e-mail
  copy: string;
}

export interface Cadence {
  key: "nutricao" | "acompanhamento";
  nome: string;
  contexto: string;
  touches: CadenceTouch[];
}

export const CADENCE_NUTRICAO: Cadence = {
  key: "nutricao",
  nome: "Nutrição",
  contexto: "Prospecção → Agendamento",
  touches: [
    {
      n: 1,
      dia: "D0",
      canal: "LinkedIn/E-mail",
      objetivo: "Abordagem inicial",
      assunto: "Conversa rápida sobre people analytics na [empresa]",
      copy:
        "Oi [nome], tudo bem? Vi que você atua como [cargo] na [empresa] e queria me apresentar — trabalho com people analytics na Minehr. [gancho específico do lead]. Faz sentido batermos um papo de 20 minutos?",
    },
    {
      n: 2,
      dia: "D+3",
      canal: "E-mail",
      objetivo: "Reforço de valor",
      assunto: "Material sobre [dor identificada] — [empresa]",
      copy:
        "Separei [ferramenta de bolso/conteúdo] sobre [dor identificada], achei que pode ajudar a [empresa] a pensar em [tema]. Fico à disposição se quiser trocar uma ideia.",
    },
    {
      n: 3,
      dia: "D+7",
      canal: "LinkedIn",
      objetivo: "Prova social",
      assunto: "",
      copy:
        "Vi um caso parecido com o cenário de vocês: uma empresa do porte da [empresa] conseguiu [resultado]. Se fizer sentido, posso te mostrar como aplicamos isso.",
    },
    {
      n: 4,
      dia: "D+14",
      canal: "E-mail",
      objetivo: "Convite direto para call",
      assunto: "20 minutos para falar de dados de pessoas?",
      copy:
        "[nome], como está a maturidade de dados de pessoas na [empresa] hoje? Consigo te mostrar em 20 minutos onde a Minehr pode ajudar — topa marcarmos?",
    },
    {
      n: 5,
      dia: "D+25",
      canal: "E-mail",
      objetivo: "Última tentativa",
      assunto: "Pausando por aqui — [empresa]",
      copy:
        "Não tivemos retorno até aqui, então vou pausar por enquanto. Fico à disposição sempre que fizer sentido revisitar o tema — um abraço!",
    },
  ],
};

export const CADENCE_ACOMPANHAMENTO: Cadence = {
  key: "acompanhamento",
  nome: "Acompanhamento",
  contexto: "Proposta → Negociação → Fechamento",
  touches: [
    {
      n: 1,
      dia: "D+1",
      canal: "E-mail",
      objetivo: "Reforçar proposta e alinhar próximos passos",
      assunto: "Proposta enviada — próximos passos",
      copy:
        "Segue a proposta alinhada na nossa conversa. Fico à disposição para adaptar o que for preciso, e animado com a possibilidade de a [empresa] contar com a Minehr em [objetivo]. Aguardo seu retorno para avançarmos!",
    },
    {
      n: 2,
      dia: "D+2",
      canal: "WhatsApp",
      objetivo: "Confirmar recebimento",
      assunto: "",
      copy:
        "Oi [nome], tudo bem? Passei só para confirmar se você conseguiu visualizar a proposta enviada por e-mail. Qualquer dúvida, fico à disposição!",
    },
    {
      n: 3,
      dia: "D+5",
      canal: "E-mail",
      objetivo: "Agregar valor com resultado real",
      assunto: "Um resultado parecido com o que propusemos",
      copy:
        "Quero compartilhar um case parecido com o que propusemos, feito com [empresa parceira]. Eles alcançaram [resultado objetivo]. Acredito que podemos gerar o mesmo tipo de impacto na [empresa].",
    },
    {
      n: 4,
      dia: "D+8",
      canal: "LinkedIn",
      objetivo: "Engajamento sutil",
      assunto: "",
      copy:
        "Curtir/comentar a publicação mais recente do ponto focal, ou reagir a uma publicação da empresa com um comentário ou pergunta genuína.",
    },
    {
      n: 5,
      dia: "D+10",
      canal: "WhatsApp",
      objetivo: "Sondar avanço interno",
      assunto: "",
      copy:
        "Oi [nome], queria entender como a proposta está circulando aí dentro. Vocês já conseguiram conversar com os outros envolvidos?",
    },
    {
      n: 6,
      dia: "D+13",
      canal: "E-mail",
      objetivo: "Criar urgência",
      assunto: "Janela de [período] — precisamos alinhar até [data]",
      copy:
        "Estamos fechando a agenda de [período/janela]. Para garantir [entrega/benefício] dentro do prazo, precisamos alinhar até [data]. Podemos marcar uma conversa até lá?",
    },
    {
      n: 7,
      dia: "D+15",
      canal: "WhatsApp",
      objetivo: "Follow-up direto",
      assunto: "",
      copy:
        "Oi [nome], tudo certo? Consegue me dizer se já tem uma definição sobre a proposta? Preciso te dar um retorno até [data] sobre [item].",
    },
    {
      n: 8,
      dia: "D+18",
      canal: "E-mail",
      objetivo: "Encerramento elegante com porta aberta",
      assunto: "Encerrando o ciclo — porta aberta",
      copy:
        "Como não tivemos avanço até aqui, vou encerrar esse ciclo de conversas por enquanto. Mas deixo a proposta à disposição caso volte a fazer sentido no futuro. Será um prazer contar com a [empresa]!",
    },
  ],
};

export const CADENCES: Cadence[] = [CADENCE_NUTRICAO, CADENCE_ACOMPANHAMENTO];

// Sugestão de cadência conforme a etapa atual (spec §6.5).
const NUTRICAO_STAGES = ["Primeiro contato", "Reunião agendada", "Reunião realizada"];
const ACOMPANHAMENTO_STAGES = [
  "Proposta em negociação",
  "Aprovação interna",
  "Proposta de renovação",
  "Negociação",
];

export function suggestedCadenceKey(stageName: string): "nutricao" | "acompanhamento" | null {
  if (NUTRICAO_STAGES.includes(stageName)) return "nutricao";
  if (ACOMPANHAMENTO_STAGES.includes(stageName)) return "acompanhamento";
  return null;
}

// Substitui [empresa], [nome], [cargo] e anexa a assinatura.
export function applySubstitutions(
  text: string,
  vars: { empresa?: string; nome?: string; cargo?: string }
): string {
  return text
    .replace(/\[empresa\]/g, vars.empresa || "[empresa]")
    .replace(/\[nome\]/g, vars.nome || "[nome]")
    .replace(/\[cargo\]/g, vars.cargo || "[cargo]");
}

export function pendingFieldCount(text: string): number {
  const matches = text.match(/\[[^\]]+\]/g);
  return matches ? matches.length : 0;
}
